import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getCookiePref } from '../data/selectors/userAuthSelectors';
import FeatureFlagPersistor from './router/FeatureFlagPersistor';

import styles from './app.module.css';

import AddPearl from './addPearl/AddPearl';
import Attributions from './attributions/Attributions';
import AutoAuth from './autoAuth/AutoAuth';
// import ClinicalPearls from './clinicalPearls/ClinicalPearls';
import ContinuingEducation from './continuingEducation/ContinuingEducation';
import CookieConsent from './cookieConsent/CookieConsent';
import Donate from './donate/Donate';
import EditProvider from './providerNetwork/EditProvider';
import Faqs from './faqs/Faqs';
import Feedback from './feedback/Feedback';
import Footer from './footer/Footer';
import ForgotPassword from './forgotPassword/ForgotPassword';
import Header from './header/Header';
import Home from './home/Home';
import IowgSurvey from './iowgSurvey/IowgSurvey';
import IpLoginNotice from './ipLoginNotice/IpLoginNotice';
import JoinNow from './joinNow/JoinNow';
import KnowAdmin from './knowAdmin/KnowAdmin';
import GeminiAdmin from './geminiAdmin/GeminiAdmin';
import License from './license/License';
import Login from './login/Login';
import Methodology from './methodology/Methodology';
import NoMatchPage from './404/NoMatchPage';
import Newsletter from './newsletter/Newsletter';
import OrgAdmin from './orgAdmin/OrgAdmin';
import PasswordReset from './passwordReset/PasswordReset';
import PatientResources from './patientResources/PatientResources';
import PrivacyPolicy from './privacyPolicy/PrivacyPolicy';
import ProviderNetwork from './providerNetwork/ProviderNetwork';
import ProviderResultDetail from './providerNetwork/ProviderResultDetail';
import RenewSub from './renewSub/RenewSub';
import RequireAuth from './requireAuth/RequireAuth';
import Signup from './signup/Signup';
import SingleResult from './singleResult/SingleResult';
import Studies from './studies/Studies';
import Chat from './chat/Chat';
import DocsIndex from './docsIndex/DocsIndex';
import SsoAdmin from './ssoAdmin/SsoAdmin';
import SsoDocs from './ssoDocs/SsoDocs';
import TermsOfUse from './termsOfUse/TermsOfUse';

// const EventTargetPrototype = document.__proto__.__proto__.__proto__.__proto__;
// const origAddEventListener = EventTargetPrototype.addEventListener;
// EventTargetPrototype.addEventListener = function addEventListenerWrapper(type, listener) {
//     if (typeof listener !== 'function') throw new Error('bad listener for ' + type);
//     return origAddEventListener.apply(this, arguments);
// };

const CTCT_POPUP_EXCLUDED_PATHS = ['/newsletter'];

function CtctPopupLoader({ cookiesAccepted }) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!cookiesAccepted) return;
    if (CTCT_POPUP_EXCLUDED_PATHS.includes(pathname)) return;
    const dismissedAt = window.localStorage.getItem('know_ctct_dismissed');
    if (dismissedAt) {
      const thirtyDays = 30 * 24 * 60 * 60 * 1000;
      if (Date.now() - parseInt(dismissedAt, 10) < thirtyDays) return;
      window.localStorage.removeItem('know_ctct_dismissed');
    }
    if (document.getElementById('signupScript')) return;

    window._ctct_m = '0432783ed4d40930d0d2824f67f11e42';
    const script = document.createElement('script');
    script.id = 'signupScript';
    script.src = '//static.ctctcdn.com/js/signup-form-widget/current/signup-form-widget.min.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    // Detect widget dismissal via DOM polling so we can suppress it on future
    // visits using our own localStorage key instead of relying on 3rd-party cookies.
    let wasVisible = false;
    const intervalId = setInterval(() => {
      const widgets = document.querySelectorAll('.ctct-popup, [class*="ctct-form"]');
      const isVisible = Array.from(widgets).some((el) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
      if (wasVisible && !isVisible) {
        window.localStorage.setItem('know_ctct_dismissed', Date.now().toString());
        clearInterval(intervalId);
      }
      if (isVisible) wasVisible = true;
    }, 1000);

    return () => clearInterval(intervalId);
  }, [cookiesAccepted, pathname]);

  return null;
}

CtctPopupLoader.propTypes = {
  cookiesAccepted: PropTypes.bool.isRequired,
};

export default function App() {
  const cookiePref = useSelector(getCookiePref);

  // cookiePref may be boolean true (live state) or string "true"/"false" (from localStorage)
  const cookiesAccepted = cookiePref === true || cookiePref === 'true';

  return (
    <div className={styles.pageContainer}>
      <Router>
        <FeatureFlagPersistor />
        <CtctPopupLoader cookiesAccepted={cookiesAccepted} />
        <Header />
        <div className={styles.contentWrap}>
          <Routes>
            <Route exact path="/" element={<Home />} />
            <Route
              exact
              path="/add-pearl"
              element={
                <RequireAuth>
                  <AddPearl />
                </RequireAuth>
              }
            />
            {/* attributions link maintained in case there are external references */}
            <Route exact path="/attributions" element={<Attributions />} />
            <Route exact path="/about-us" element={<Attributions />} />
            <Route exact path="/autoauth" element={<AutoAuth />} />
            <Route
              exact
              path="/continuing-education"
              element={
                <RequireAuth>
                  <ContinuingEducation />
                </RequireAuth>
              }
            />
            <Route exact path="/donate" element={<Donate />} />
            <Route
              exact
              path="/edit-provider"
              element={
                <RequireAuth>
                  <EditProvider />
                </RequireAuth>
              }
            />
            <Route exact path="/faqs" element={<Faqs />} />
            <Route exact path="/feedback" element={<Feedback />} />
            <Route exact path="/forgot-password" element={<ForgotPassword />} />
            <Route exact path="/iowg-survey" element={<IowgSurvey />} />
            <Route exact path="/join-now" element={<JoinNow />} />
            <Route exact path="/know-admin" element={<KnowAdmin />} />
            <Route exact path="/know-admin/sso" element={<SsoAdmin />} />
            <Route exact path="/gemini-admin" element={<GeminiAdmin />} />
            <Route exact path="/license" element={<License />} />
            <Route exact path="/login" element={<Login />} />
            <Route exact path="/methodology" element={<Methodology />} />
            <Route exact path="/newsletter" element={<Newsletter />} />
            <Route exact path="/org-admin" element={<OrgAdmin />} />
            <Route exact path="/patient-resources" element={<PatientResources />} />
            {/* <Route exact path='/pearls' element={<ClinicalPearls />} /> */}
            <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route exact path="/provider/:slug" element={<ProviderResultDetail />} />
            <Route exact path="/providers" element={<ProviderNetwork />} />
            <Route exact path="/renew-subscription" element={<RenewSub />} />
            <Route exact path="/reset-password" element={<PasswordReset />} />
            <Route
              exact
              path="/shared"
              element={
                <RequireAuth>
                  <SingleResult />
                </RequireAuth>
              }
            />
            <Route exact path="/signup" element={<Signup />} />
            <Route
              exact
              path="/studies"
              element={
                <RequireAuth>
                  <Studies />
                </RequireAuth>
              }
            />
            <Route
              exact
              path="/chat"
              element={
                <RequireAuth>
                  <Chat />
                </RequireAuth>
              }
            />
            <Route exact path="/docs" element={<DocsIndex />} />
            <Route exact path="/docs/sso-admin-guide" element={<SsoDocs />} />
            <Route exact path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="*" element={<NoMatchPage />} />
          </Routes>
        </div>
        <IpLoginNotice />
        <CookieConsent />
        <Footer />
      </Router>
    </div>
  );
}
